import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Alert,
  AlertTitle,
  TextField,
  CircularProgress,
  Chip,
  alpha,
  useTheme,
  Card,
  CardContent,
  InputAdornment,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  CloudUpload,
  Visibility,
  VisibilityOff,
  Security,
  Download,
  VpnKey,
  Description,
  Warning,
  CheckCircle,
  Info,
  ContentCopy,
  FolderZip,
  AccountBalance,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { certificatesApi } from '@/services/api';

interface CertInfo {
  serialNumber: string;
  subject: string;
  subjectOrg: string;
  subjectFull: { name: string; value: string }[];
  issuer: string;
  issuerOrg: string;
  issuerFull: { name: string; value: string }[];
  thumbprint: string;
  thumbprint256: string;
  validFrom: string;
  expiresAt: string;
  isExpired: boolean;
  daysUntilExpiry: number;
}

interface ConvertedFormats {
  certPem: string;
  certDer: string;
  keyPem: string | null;
  caPem: string | null;
  fullChainPem: string;
}

interface ConvertResult {
  formats: ConvertedFormats;
  info: CertInfo;
}

interface DownloadOption {
  label: string;
  description: string;
  extension: string;
  getData: () => string | null;
  isBinary?: boolean;
  isPrivateKey?: boolean;
  icon: React.ReactElement;
}

const CertificateConverterPage: React.FC = () => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ConvertResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    if (selected) {
      const validExtensions = ['.pfx', '.p12'];
      const ext = selected.name.toLowerCase().slice(selected.name.lastIndexOf('.'));
      if (!validExtensions.includes(ext)) {
        toast.error('Selecione um arquivo .pfx ou .p12');
        return;
      }
      setFile(selected);
      setResult(null);
      setError(null);
    }
  };

  const handleConvert = async () => {
    if (!file || !password) {
      toast.error('Selecione um arquivo e informe a senha');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const arrayBuffer = reader.result as ArrayBuffer;
          const bytes = new Uint8Array(arrayBuffer);
          let binary = '';
          bytes.forEach(b => binary += String.fromCharCode(b));
          resolve(btoa(binary));
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });

      const response = await certificatesApi.convertPfx({ certificate: base64, password });

      if (response.success && response.data) {
        setResult(response.data as ConvertResult);
        toast.success('Certificado convertido com sucesso!');
      } else {
        const msg = (response as any).error?.message || 'Erro ao converter certificado';
        setError(msg);
        toast.error(msg);
      }
    } catch (err: any) {
      const msg = err?.error?.message || err?.message || 'Erro ao converter certificado';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setPassword('');
    setResult(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadFile = (filename: string, content: string, isBinary = false) => {
    let blob: Blob;
    if (isBinary) {
      const binary = atob(content);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      blob = new Blob([bytes], { type: 'application/octet-stream' });
    } else {
      blob = new Blob([content], { type: 'text/plain' });
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado para a area de transferencia`);
  };

  const baseName = file?.name.replace(/\.(pfx|p12)$/i, '') || 'certificado';

  const publicKeyOptions: DownloadOption[] = result
    ? [
        {
          label: 'Chave Publica (.PEM)',
          description: 'Formato aceito pelo Portal Developers Sicoob',
          extension: '.pem',
          getData: () => result.formats.certPem,
          icon: <Security />,
        },
        {
          label: 'Chave Publica (.CRT)',
          description: 'Formato X.509 - aceito pelo Sicoob',
          extension: '.crt',
          getData: () => result.formats.certPem,
          icon: <Security />,
        },
        {
          label: 'Chave Publica (.CER)',
          description: 'Formato alternativo - aceito pelo Sicoob',
          extension: '.cer',
          getData: () => result.formats.certPem,
          icon: <Security />,
        },
      ]
    : [];

  const otherOptions: DownloadOption[] = result
    ? [
        {
          label: 'Certificado DER',
          description: 'Formato binario (usado em Java/Android)',
          extension: '.der',
          getData: () => result.formats.certDer,
          isBinary: true,
          icon: <FolderZip />,
        },
        {
          label: 'Chave Privada',
          description: 'Chave privada RSA em formato PEM',
          extension: '.key',
          getData: () => result.formats.keyPem,
          isPrivateKey: true,
          icon: <VpnKey />,
        },
        {
          label: 'Cadeia CA',
          description: 'Certificados intermediarios da cadeia',
          extension: '-ca.pem',
          getData: () => result.formats.caPem,
          icon: <Description />,
        },
        {
          label: 'Cadeia Completa',
          description: 'Certificado + cadeia intermediaria',
          extension: '-fullchain.pem',
          getData: () => result.formats.fullChainPem,
          icon: <Description />,
        },
      ]
    : [];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Conversor de Certificados
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Extraia a chave publica do seu certificado PFX/P12 para vincular no Portal Developers do Sicoob
        </Typography>
      </Box>

      {/* Sicoob Context Alert */}
      <Alert
        severity="info"
        icon={<AccountBalance />}
        sx={{ mb: 3 }}
      >
        <AlertTitle>Portal Developers Sicoob</AlertTitle>
        Para criar um aplicativo no Portal Developers, voce precisa vincular a <strong>chave publica</strong> do
        certificado digital no formato <strong>.PEM</strong>, <strong>.CRT</strong> ou <strong>.CER</strong>.
        Use esta ferramenta para extrair a chave publica do seu certificado PFX.
        <strong> Nunca compartilhe a chave privada com o Sicoob ou terceiros.</strong>
      </Alert>

      <Grid container spacing={3}>
        {/* Upload Section */}
        <Grid item xs={12} md={result ? 5 : 8}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Arquivo do Certificado
            </Typography>

            {/* File Upload Area */}
            <Box
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: `2px dashed ${file ? theme.palette.primary.main : theme.palette.divider}`,
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: file
                  ? alpha(theme.palette.primary.main, 0.04)
                  : alpha(theme.palette.action.hover, 0.02),
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                },
                mb: 2,
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pfx,.p12"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <CloudUpload
                sx={{
                  fontSize: 40,
                  color: file ? 'primary.main' : 'text.disabled',
                  mb: 1,
                }}
              />
              {file ? (
                <>
                  <Typography variant="body1" fontWeight={600} color="primary">
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(file.size / 1024).toFixed(1)} KB - Clique para trocar
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body1" color="text.secondary">
                    Clique para selecionar um arquivo
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Formatos aceitos: .pfx, .p12
                  </Typography>
                </>
              )}
            </Box>

            {/* Password Field */}
            <TextField
              fullWidth
              label="Senha do Certificado"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Informe a senha do certificado PFX"
              size="small"
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleConvert}
                disabled={!file || !password || loading}
                startIcon={loading ? <CircularProgress size={18} /> : <Security />}
                fullWidth
              >
                {loading ? 'Convertendo...' : 'Converter'}
              </Button>
              {result && (
                <Button variant="outlined" onClick={handleReset} color="inherit">
                  Limpar
                </Button>
              )}
            </Box>

            {/* Error */}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {/* Security Notice */}
            <Alert severity="info" icon={<Info />} sx={{ mt: 2 }}>
              <AlertTitle>Processamento seguro</AlertTitle>
              O certificado e enviado ao servidor apenas para conversao. Nenhum dado e armazenado em disco ou banco de dados.
            </Alert>
          </Paper>
        </Grid>

        {/* Certificate Info */}
        {result && (
          <Grid item xs={12} md={7}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                mb: 3,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CheckCircle color="success" />
                <Typography variant="subtitle1" fontWeight={600}>
                  Informacoes do Certificado
                </Typography>
                <Chip
                  label={result.info.isExpired ? 'Expirado' : `${result.info.daysUntilExpiry} dias restantes`}
                  size="small"
                  color={result.info.isExpired ? 'error' : result.info.daysUntilExpiry < 30 ? 'warning' : 'success'}
                />
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Titular (CN)
                  </Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-all' }}>
                    {result.info.subject || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Organizacao
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {result.info.subjectOrg || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Emissor
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {result.info.issuer || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Organizacao Emissora
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {result.info.issuerOrg || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Valido de
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {format(new Date(result.info.validFrom), "dd/MM/yyyy 'as' HH:mm", { locale: ptBR })}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Valido ate
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {format(new Date(result.info.expiresAt), "dd/MM/yyyy 'as' HH:mm", { locale: ptBR })}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Numero de Serie
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      wordBreak: 'break-all',
                    }}
                  >
                    {result.info.serialNumber}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Thumbprint (SHA-1)
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.7rem',
                          wordBreak: 'break-all',
                        }}
                      >
                        {result.info.thumbprint}
                      </Typography>
                    </Box>
                    <Tooltip title="Copiar">
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(result.info.thumbprint, 'Thumbprint')}
                        sx={{ mt: 1.5 }}
                      >
                        <ContentCopy sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Public Key Downloads - Sicoob */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.success.main, 0.02),
                mb: 3,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <AccountBalance sx={{ color: 'success.main' }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  Chave Publica (para Sicoob)
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Baixe em qualquer um dos formatos abaixo e vincule no Portal Developers ao criar o aplicativo.
                O arquivo contem apenas o certificado publico (BEGIN CERTIFICATE).
              </Typography>

              <Grid container spacing={1.5}>
                {publicKeyOptions.map((option) => {
                  const data = option.getData();
                  const available = data !== null;

                  return (
                    <Grid item xs={12} sm={4} key={option.extension}>
                      <Card
                        variant="outlined"
                        sx={{
                          borderColor: alpha(theme.palette.success.main, 0.3),
                        }}
                      >
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Box sx={{ color: 'success.main', display: 'flex' }}>
                              {option.icon}
                            </Box>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="body2" fontWeight={600}>
                                {option.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                {option.description}
                              </Typography>
                            </Box>
                          </Box>

                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            fullWidth
                            disabled={!available}
                            startIcon={<Download />}
                            onClick={() => {
                              if (data) {
                                downloadFile(`${baseName}${option.extension}`, data);
                                toast.success(`${option.label} baixado`);
                              }
                            }}
                            sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                          >
                            {`${baseName}${option.extension}`}
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>

            {/* Other Formats */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
              }}
            >
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                Outros Formatos
              </Typography>

              <Grid container spacing={1.5}>
                {otherOptions.map((option) => {
                  const data = option.getData();
                  const available = data !== null;

                  return (
                    <Grid item xs={12} sm={6} key={option.extension}>
                      <Card
                        variant="outlined"
                        sx={{
                          opacity: available ? 1 : 0.5,
                          borderColor: option.isPrivateKey
                            ? alpha(theme.palette.warning.main, 0.4)
                            : theme.palette.divider,
                          backgroundColor: option.isPrivateKey
                            ? alpha(theme.palette.warning.main, 0.02)
                            : 'transparent',
                        }}
                      >
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Box
                              sx={{
                                color: option.isPrivateKey ? 'warning.main' : 'primary.main',
                                display: 'flex',
                              }}
                            >
                              {option.icon}
                            </Box>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="body2" fontWeight={600}>
                                {option.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                {option.description}
                              </Typography>
                            </Box>
                          </Box>

                          {option.isPrivateKey && available && (
                            <Alert
                              severity="error"
                              icon={<Warning sx={{ fontSize: 16 }} />}
                              sx={{
                                py: 0,
                                px: 1,
                                mb: 1,
                                '& .MuiAlert-message': { fontSize: '0.7rem', py: 0.5 },
                              }}
                            >
                              NUNCA envie este arquivo para o Sicoob ou terceiros!
                            </Alert>
                          )}

                          <Button
                            variant={option.isPrivateKey ? 'outlined' : 'contained'}
                            color={option.isPrivateKey ? 'warning' : 'primary'}
                            size="small"
                            fullWidth
                            disabled={!available}
                            startIcon={<Download />}
                            onClick={() => {
                              if (data) {
                                downloadFile(
                                  `${baseName}${option.extension}`,
                                  data,
                                  option.isBinary
                                );
                                toast.success(`${option.label} baixado`);
                              }
                            }}
                            sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                          >
                            {available ? `${baseName}${option.extension}` : 'Indisponivel'}
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default CertificateConverterPage;
